import { useCallback, useRef, useState, type JSX } from 'react';

import type { Blueprint } from '@rta/rta-blueprint-library';
import { captureOverheadPng } from './capture-overhead-png';
import { HeadlessOverheadCaptureRenderer } from './headless-overhead-capture-renderer';
import type {
	CaptureOverheadParams,
	CapturedOverhead,
	UseHeadlessOverheadCaptureParams,
	UseHeadlessOverheadCaptureReturn
} from './types';

type Job = {
	jobId: string;
	blueprint: Blueprint;
	width: number;
	height: number;
	scale: number;
	resolve: (result: CapturedOverhead) => void;
};

const DEFAULT_WIDTH = 1600;
const DEFAULT_HEIGHT = 1200;
const DEFAULT_SCALE = 2;
const DEFAULT_CONCURRENCY = 3;

/**
 * Headless 2D overhead capture hook. Mirrors the 3D `useHeadlessCapture` pattern but
 * runs multiple captures concurrently (no WebGL constraint) up to `concurrency`.
 *
 * Each call to `captureOverhead` enqueues a job. Up to `concurrency` jobs render in
 * parallel via off-screen portals; when one finishes its slot is reused by the next
 * queued job.
 */
export const useHeadlessOverheadCapture = (
	params: UseHeadlessOverheadCaptureParams = {}
): UseHeadlessOverheadCaptureReturn => {
	const { concurrency = DEFAULT_CONCURRENCY, assetsBaseUrl } = params;

	const [jobs, setJobs] = useState<Array<Job>>([]);
	const jobsRef = useRef<Array<Job>>([]);
	jobsRef.current = jobs;

	const completedRef = useRef<Set<string>>(new Set());

	const captureOverhead = useCallback((p: CaptureOverheadParams): Promise<CapturedOverhead> => {
		const { blueprint, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT, scale = DEFAULT_SCALE } = p;
		return new Promise<CapturedOverhead>((resolve) => {
			const jobId = `${blueprint.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
			setJobs((prev) => [...prev, { jobId, blueprint, width, height, scale, resolve }]);
		});
	}, []);

	const handleReady = useCallback(async (jobId: string, container: HTMLDivElement, scale: number): Promise<void> => {
		// React StrictMode (and effect re-runs) can fire this twice for the same job — dedupe.
		if (completedRef.current.has(jobId)) return;
		completedRef.current.add(jobId);

		let png = '';
		try {
			png = await captureOverheadPng({ container, scale });
		} catch {
			png = '';
		}

		const job = jobsRef.current.find((j) => j.jobId === jobId);
		if (job) job.resolve({ png });

		setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
		// Free the dedupe entry shortly after — by the time we re-add a job with the same id
		// (different timestamp), it'll be a different id anyway, but be tidy.
		setTimeout(() => completedRef.current.delete(jobId), 1000);
	}, []);

	const active = jobs.slice(0, concurrency);

	const CapturePortal = active.length > 0
		? (
			<>
				{active.map((job) => (
					<HeadlessOverheadCaptureRenderer
						key={job.jobId}
						blueprint={job.blueprint}
						width={job.width}
						height={job.height}
						assetsBaseUrl={assetsBaseUrl}
						onReady={(container) => { void handleReady(job.jobId, container, job.scale); }}
					/>
				))}
			</>
		)
		: null;

	return {
		captureOverhead,
		CapturePortal: CapturePortal as JSX.Element | null,
		isCapturing: jobs.length > 0
	};
};
